import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { LogOut, LayoutDashboard, Building2, FileText, Shield, Palette, Info, Users, Bell } from 'lucide-react';
import { Button } from './ui';
import { cn } from '../utils/cn';
import scoutLogo from '../assets/images/Scout Logo.png';
import { Hotel } from '../types';
import { getHotels } from '../firebase/firestore';
import { hotelNameToSlug } from '../utils/format';
import { NotificationBadge } from './NotificationBadge';

const navigation: Array<{
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  showNotification?: boolean;
}> = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Hotels', href: '/hotels', icon: Building2 },
  { name: 'Templates', href: '/templates', icon: FileText },
  { name: 'Widget Customizer', href: '/widget-customizer', icon: Palette },
  { name: 'Meet Scout', href: '/meet-scout', icon: Users },
  { name: 'Info', href: '/info', icon: Info },
  { name: 'Notifications', href: '/notifications', icon: Bell, showNotification: true },
  { name: 'Admin', href: '/admin', icon: Shield },
];

export function Sidebar() {
  const location = useLocation();
  const { user, isAdmin, isManagement, isHotelStaff, assignedHotelIds, isMimicking, mimickedUser, stopMimicking, logout } = useUser();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch hotels data for hotel staff users
  useEffect(() => {
    const fetchHotels = async () => {
      if (isHotelStaff && assignedHotelIds.length > 0) {
        try {
          setLoading(true);
          const allHotels = await getHotels();
          // Filter hotels to only show assigned ones
          const assignedHotels = allHotels.filter((hotel: Hotel) => 
            assignedHotelIds.includes(hotel.id)
          );
          setHotels(assignedHotels);
        } catch (error) {
          console.error('Error fetching hotels:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchHotels();
  }, [isHotelStaff, assignedHotelIds]);

  const getNavigationItems = () => {
    // Always include Dashboard for all users
    const dashboardItem = { name: 'Dashboard', href: '/', icon: LayoutDashboard };
    
    // Get additional navigation items for hotel staff (excluding Dashboard, Hotels, Templates, Admin, and Notifications)
    const hotelStaffNavItems = navigation.filter(item => 
      item.name !== 'Hotels' && 
      item.name !== 'Dashboard' && 
      item.name !== 'Templates' && 
      item.name !== 'Admin' &&
      item.name !== 'Notifications'
    );
    
    // Get additional navigation items for management (excluding Dashboard, Hotels, Templates, Admin, and Notifications)
    const managementNavItems = navigation.filter(item => 
      item.name !== 'Hotels' && 
      item.name !== 'Dashboard' && 
      item.name !== 'Templates' && 
      item.name !== 'Admin' &&
      item.name !== 'Notifications'
    );
    
    // Get additional navigation items for admin (excluding Dashboard and Hotels)
    const adminNavItems = navigation.filter(item => item.name !== 'Hotels' && item.name !== 'Dashboard');
    
    if (isHotelStaff) {
      if (assignedHotelIds.length === 1 && hotels.length === 1) {
        // Single hotel: show hotel name + hotel staff nav items
        const hotel = hotels[0];
        const hotelSlug = hotelNameToSlug(hotel.name);
        return [
          { name: hotel.name, href: `/hotel/${hotelSlug}`, icon: Building2 },
          ...hotelStaffNavItems,
        ];
      } else if (hotels.length > 1) {
        // Multiple hotels: show "Hotels" overview + individual hotel buttons + hotel staff nav items
        const hotelButtons = hotels.map(hotel => {
          const hotelSlug = hotelNameToSlug(hotel.name);
          return {
            name: hotel.name,
            href: `/hotel/${hotelSlug}`,
            icon: Building2,
            isHotel: true
          };
        });
        
        return [
          { name: 'Hotels', href: '/hotels', icon: Building2 },
          ...hotelButtons,
          ...hotelStaffNavItems,
        ];
      } else if (loading) {
        // Loading state: show loading + hotel staff nav items
        return [
          { name: 'Loading...', href: '/hotels', icon: Building2 },
          ...hotelStaffNavItems,
        ];
      } else {
        // Fallback if no hotels found: show Hotels + hotel staff nav items
        return [
          { name: 'Hotels', href: '/hotels', icon: Building2 },
          ...hotelStaffNavItems,
        ];
      }
    }
    
    if (isManagement) {
      // Management navigation - include Dashboard, exclude Hotels since Dashboard shows all
      return [dashboardItem, ...managementNavItems];
    }
    
    // Admin navigation - include Dashboard, exclude Hotels since Dashboard shows all
    return [dashboardItem, ...adminNavItems];
  };

  return (
    <div className="fixed inset-y-4 left-4 w-64 bg-white/15 backdrop-blur-3xl rounded-3xl shadow-2xl border border-white/40 overflow-hidden">

      
      <div className="flex flex-col h-full relative z-10">
        {/* Logo Section */}
        <div className="flex items-center px-6 py-8 border-b border-white/20">
          <div className="flex items-center space-x-4 group cursor-pointer">
            {isMimicking && (
              <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-semibold animate-pulse">
                Mimicking {mimickedUser?.displayName}
              </div>
            )}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-400/30 to-primary-500/20 rounded-2xl blur-lg group-hover:blur-xl transition-all duration-500" />
              <img 
                src={scoutLogo} 
                alt="Scout Logo" 
                className="relative w-14 h-14 object-contain drop-shadow-lg group-hover:drop-shadow-xl transition-all duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <div className="space-y-1">
              <h1 className="text-2xl font-display font-bold bg-gradient-to-r from-neutral-900 to-neutral-700 bg-clip-text text-transparent tracking-tight leading-none group-hover:from-neutral-900 group-hover:to-neutral-800 transition-all duration-300">
                Scout AI
              </h1>
              <p className="text-xs font-text font-semibold text-neutral-500 tracking-wider group-hover:text-neutral-600 transition-colors duration-300">
                Project Overview
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1">
          {getNavigationItems()
            .map((item) => {
              // Dashboard is active for root, /dashboard, and /hotels paths
              const isActive = item.href === '/' 
                ? ['/', '/dashboard', '/hotels'].includes(location.pathname)
                : location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center px-4 py-3.5 text-sm font-medium rounded-2xl transition-all duration-300 group relative overflow-hidden',
                    isActive
                      ? 'bg-gradient-to-r from-primary-400/90 to-primary-500/90 text-white shadow-lg shadow-primary-400/30 scale-[1.02]'
                      : 'text-neutral-700 hover:text-neutral-900 hover:bg-white/30 hover:backdrop-blur-sm hover:shadow-md hover:shadow-black/5 hover:scale-[1.01]'
                  )}
                >
                  {/* Active state background glow */}
                  {isActive && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  )}
                  
                  <div className="relative">
                    <item.icon 
                      className={cn(
                        'w-5 h-5 mr-3.5 transition-all duration-300',
                        isActive 
                          ? 'text-white drop-shadow-sm' 
                          : 'text-neutral-500 group-hover:text-neutral-700 group-hover:scale-110'
                      )} 
                    />
                  </div>
                  <span className="tracking-wide font-medium flex items-center">
                    {item.name}
                    {'showNotification' in item && item.showNotification && <NotificationBadge className="ml-2" showIcon={false} />}
                  </span>
                  
                  {/* Subtle indicator for active state */}
                  {isActive && (
                    <div className="absolute right-3 w-2 h-2 bg-white rounded-full shadow-sm" />
                  )}
                </Link>
              );
            })}
        </nav>

        {/* User Section */}
        <div className="px-4 py-4 border-t border-white/20">
          {isAdmin ? (
            <Link to="/admin" className="block">
              <div className="flex items-center space-x-3 px-3 py-3 rounded-2xl bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all duration-300 cursor-pointer group">
                {user?.photoURL ? (
                  <div className="relative">
                    <img 
                      src={user.photoURL} 
                      alt={user.displayName || 'User'} 
                      className="w-10 h-10 rounded-xl object-cover border border-white/30 shadow-sm group-hover:shadow-md transition-shadow duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-400/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-neutral-100 to-neutral-200 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-300 border border-white/30">
                    <span className="text-sm font-semibold text-neutral-700">A</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 truncate">{user?.displayName || 'Admin User'}</p>
                  <p className="text-xs text-neutral-600 truncate">{user?.email || 'admin@hotelai.com'}</p>
                </div>
                <div className="w-2 h-2 bg-primary-400 rounded-full shadow-sm" />
              </div>
            </Link>
          ) : (
            <div className="flex items-center space-x-3 px-3 py-3 rounded-2xl bg-white/20 backdrop-blur-sm">
              {user?.photoURL ? (
                <div className="relative">
                  <img 
                    src={user.photoURL} 
                    alt={user.displayName || 'User'} 
                    className="w-10 h-10 rounded-xl object-cover border border-white/30 shadow-sm"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-400/20 to-transparent rounded-xl" />
                </div>
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-neutral-100 to-neutral-200 rounded-xl flex items-center justify-center shadow-sm border border-white/30">
                  <span className="text-sm font-semibold text-neutral-700">H</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-neutral-900 truncate">{user?.displayName || 'Hotel Staff'}</p>
                <p className="text-xs text-neutral-600 truncate">{user?.email || 'staff@hotel.com'}</p>
              </div>
              <div className="w-2 h-2 bg-neutral-400 rounded-full shadow-sm" />
            </div>
          )}
          
          {/* Stop Mimicking Button */}
          {isMimicking && (
            <div className="mt-3">
              <Button
                onClick={stopMimicking}
                variant="ghost"
                className="w-full justify-start px-3 py-3 text-sm font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50/20 rounded-2xl transition-all duration-300 group border border-orange-200/30"
              >
                <LogOut className="w-4 h-4 mr-3 transition-transform duration-300 group-hover:-translate-x-0.5" />
                Stop Mimicking
              </Button>
            </div>
          )}
          
          {/* Logout Button */}
          <div className="mt-3">
            <Button
              onClick={logout}
              variant="ghost"
              className="w-full justify-start px-3 py-3 text-sm font-medium text-neutral-600 hover:text-neutral-900 hover:bg-white/20 rounded-2xl transition-all duration-300 group"
            >
              <LogOut className="w-4 h-4 mr-3 transition-transform duration-300 group-hover:-translate-x-0.5" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
